import { useCallback, useEffect, useRef, useState } from 'react';

import type { UseRestTimer } from './useRestTimer';

/** The offer on the running rest: how many seconds to add, and a key that
 *  changes with every new offer so the cue and the announcement restart. */
export type RestInvite = { sec: number; key: number };

export type UseRestInvite = {
  /** The live offer, or null. Never shown outside a running rest. */
  invite: RestInvite | null;
  /** Offer `sec` more seconds on the rest just armed. 0 withdraws it. */
  offer: (sec: number) => void;
  /** The athlete took it: the rest grows by the offer, the offer goes. */
  accept: () => void;
  /** Withdraw without touching the timer (a rest started by hand). */
  clear: () => void;
};

// The invite to rest longer after a set near failure. It lives above the Modal
// beside the rest timer and restAdvance (LG-13): a minimize mid-rest must not
// drop an offer while the countdown it belongs to keeps running.
//
// ⛔ It never changes the timer by itself. Ignored, nothing happens; it goes
// when the rest ends or is skipped, which is what `resting` turning false says.
export function useRestInvite({
  resting,
  restTimer,
}: {
  /** restAdvance.resting: a countdown is running or paused. */
  resting: boolean;
  /** Only `add` is read, and only when the offer is taken. */
  restTimer: Pick<UseRestTimer, 'add'>;
}): UseRestInvite {
  const [invite, setInvite] = useState<RestInvite | null>(null);
  const keyRef = useRef(0);

  const offer = useCallback((sec: number) => {
    keyRef.current += 1;
    setInvite(sec > 0 ? { sec, key: keyRef.current } : null);
  }, []);
  const clear = useCallback(() => setInvite(null), []);
  const accept = useCallback(() => {
    if (invite === null) return;
    restTimer.add(invite.sec);
    setInvite(null);
  }, [invite, restTimer]);

  // Cleared on the running → over EDGE, not on every idle render: re-arming
  // from the "Done" grace window reads idle for one commit, and clearing there
  // would drop the offer the new set just made.
  const wasResting = useRef(resting);
  useEffect(() => {
    const was = wasResting.current;
    wasResting.current = resting;
    if (was && !resting) setInvite(null);
  }, [resting]);

  return { invite: resting ? invite : null, offer, accept, clear };
}
